import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ClientHeader from "../../../../common/ClientHeader";
import ProtectedPage from "../../../../common/ProtectedPage";
import CommandsQueriesClient from "../../../../../requests/queries/CommandsQueries";
import LoadingSpinner from "../../../../common/Loading";
import { ProductsQueriesClient } from "../../../../../requests/queries/ProductsQueries";
import "./ProductPageStyles.css";
import { convertColorToHex } from "../../../../../utils/utils";
import Sidebar from "../../../../common/Sidebar";

const ProductPageContent = ({ currentCommand, setCurrentCommand }) => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedPrice, setSelectedPrice] = useState(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setIsLoading(true);
                const res = await ProductsQueriesClient.findById(id);
                
                if (res.data && res.data.product) {
                    const productData = res.data.product;
                    setProduct(productData);
                    
                    if (productData.variants && productData.variants.length > 0) {
                        setSelectedVariant(productData.variants[0]);
                    }
                    
                    if (productData.prices && productData.prices.length > 0) {
                        const defaultPrice = productData.prices.find(p => p.id === productData.default_price_id) 
                            || productData.prices[0];
                        setSelectedPrice(defaultPrice);
                    }
                    
                    if (productData.images && productData.images.length > 0) {
                        const defaultImageIndex = productData.images.findIndex(
                            img => img.id === productData.default_image_id
                        );
                        if (defaultImageIndex >= 0) {
                            setSelectedImageIndex(defaultImageIndex);
                        }
                    }
                } else {
                    setError("Product not found");
                }
            } catch (err) {
                console.error("Erreur lors du chargement du produit:", err);
                setError("Error loading product");
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchProduct();
        }
    }, [id]);

    const handleAddToCart = async () => {
        if (product.variants && product.variants.length > 0 && !selectedVariant) {
            alert("Please select a variant before adding to cart");
            return;
        }

        if (isVariantInCommand()) {
            alert("This variant is already in your cart");
            return;
        }

        const variantId = selectedVariant ? selectedVariant.id : product.id;

        try {
            if(!currentCommand) {
                const res = await CommandsQueriesClient.create();
                if(res && res.data && res.data.command) {
                    await CommandsQueriesClient.editProductsForCommand(res.data.command.id, variantId, 1);
                    // Attendre un peu pour que l'API mette à jour
                    await new Promise(resolve => setTimeout(resolve, 300));
                    // Récupérer la commande mise à jour avec le produit
                    const updatedCommand = await CommandsQueriesClient.findById(res.data.command.id);
                    if(updatedCommand && updatedCommand.data && updatedCommand.data.command) {
                        setCurrentCommand(updatedCommand.data.command);
                    }
                }
            } else {
                await CommandsQueriesClient.editProductsForCommand(currentCommand.id, variantId, 1);
                // Attendre un peu pour que l'API mette à jour
                await new Promise(resolve => setTimeout(resolve, 300));
                // Récupérer la commande mise à jour avec le produit
                const updatedCommand = await CommandsQueriesClient.findById(currentCommand.id);
                if(updatedCommand && updatedCommand.data && updatedCommand.data.command) {
                    setCurrentCommand(updatedCommand.data.command);
                }
            }
        } catch (error) {
            alert("Error adding to cart");
        }
    };

    const handleVariantChange = (variant) => {
        setSelectedVariant(variant);
    };

    const getTotalStock = () => {
        if (!product || !product.variants) return 0;
        return product.variants.reduce((sum, variant) => sum + (variant.stock || 0), 0);
    };

    const isAvailable = () => {
        if (!product) return false;

        if (product.variants && product.variants.length > 0) {
            if (!selectedVariant) return false;
            if (selectedVariant.stock <= 0) return false;
            if (isVariantInCommand()) return false;
            return true;
        }
        
        if (getTotalStock() <= 0) return false;
        if (isProductInCommand()) return false;
        return true;
    };

    const isVariantInCommand = () => {
        if (!currentCommand?.products || !selectedVariant) return false;
        
        return currentCommand.products.some(prod => {
            if (prod.id === product.id && prod.variants) {
                return prod.variants.some(variant => variant.id === selectedVariant.id);
            }
            return false;
        });
    };

    const isProductInCommand = () => {
        if (!currentCommand?.products) return false;
        
        if (!product.variants || product.variants.length === 0) {
            return currentCommand.products.some(prod => prod.id === product.id);
        }
        
        return isVariantInCommand();
    };

    if (isLoading) {
        return (
            <>
                <ClientHeader currentCommand={currentCommand} setCurrentCommand={setCurrentCommand} />
                <LoadingSpinner style={{height: '100vh', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000}} />
            </>
        );
    }

    if (error || !product) {
        return (
            <>
                <ClientHeader currentCommand={currentCommand} setCurrentCommand={setCurrentCommand} />
                <div className="product-container">
                    <div className="product-error">
                        <i className="bi bi-exclamation-triangle"></i>
                        <h2>Product not found</h2>
                        <p>{error || "The product you are looking for does not exist or has been removed."}</p>
                        <button className="back-btn" onClick={() => navigate("/shop")}>
                            <i className="bi bi-arrow-left"></i>
                            Back to shop
                        </button>
                    </div>
                </div>
            </>
        );
    }

    const mainImage = product.images && product.images.length > 0 
        ? product.images[selectedImageIndex]?.image_url 
        : null;

    // Grouper les variantes par couleur
    const variantsByColor = product.variants ? product.variants.reduce((acc, variant) => {
        const color = variant.color || 'default';
        if (!acc[color]) acc[color] = [];
        acc[color].push(variant);
        return acc;
    }, {}) : {};

    const colors = Object.keys(variantsByColor);
    const selectedColor = selectedVariant?.color || colors[0];
    const sizesForColor = variantsByColor[selectedColor] || [];

    const handlePreviousImage = () => {
        if (product.images && product.images.length > 0) {
            setSelectedImageIndex((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
        }
    };

    const handleNextImage = () => {
        if (product.images && product.images.length > 0) {
            setSelectedImageIndex((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
        }
    };

    return (
        <>
            <div className="patricians-layout">
                <ClientHeader currentCommand={currentCommand} setCurrentCommand={setCurrentCommand} />
                <div className="patricians-main-content">
                    {/* Sidebar gauche avec catégories */}
                    <Sidebar />

                    {/* Zone principale avec le produit */}
                    <div className="patricians-product-page">
                        <div className="patricians-product-container">
                        {/* Images à gauche */}
                        <div className="patricians-product-images">
                            {product.images && product.images.length > 0 ? (
                                <>
                                    {product.images.length > 1 && (
                                        <button 
                                            className="patricians-image-nav patricians-image-nav-left"
                                            onClick={handlePreviousImage}
                                            aria-label="Previous image"
                                        >
                                            ←
                                        </button>
                                    )}
                                    <div className="patricians-main-image">
                                        <img
                                            src={mainImage} 
                                            alt={product.name}
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/600x600?text=Image+non+disponible';
                                            }}
                                        />
                                    </div>
                                    {product.images.length > 1 && (
                                        <button 
                                            className="patricians-image-nav patricians-image-nav-right"
                                            onClick={handleNextImage}
                                            aria-label="Next image"
                                        >
                                            →
                                        </button>
                                    )}
                                    {product.images.length > 1 && (
                                        <div className="patricians-image-thumbnails">
                                            {product.images.map((image, index) => (
                                                <div
                                                    key={image.id}
                                                    className={`patricians-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                                                    onClick={() => setSelectedImageIndex(index)}
                                                >
                                                    <img
                                                        src={image.image_url} 
                                                        alt={`${product.name} - Vue ${index + 1}`}
                                                        onError={(e) => {
                                                            e.target.src = 'https://via.placeholder.com/100x100?text=Image';
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="patricians-main-image patricians-no-image">
                                    <p>No image available</p>
                                </div>
                            )}
                        </div>

                        {/* Détails à droite */}
                        <div className="patricians-product-details">
                            <h1 className="patricians-product-title">{product.name}</h1>
                            {selectedVariant?.color && (
                                <p className="patricians-product-color">[{selectedVariant.color.toUpperCase()}]</p>
                            )}
                            {selectedPrice && (
                                <p className="patricians-product-price">
                                    €{(() => {
                                        const price = selectedPrice.price;
                                        if (price) {
                                            const numPrice = typeof price === 'string' ? parseFloat(price) : price;
                                            return isNaN(numPrice) ? '0,00' : numPrice.toFixed(2).replace('.', ',');
                                        }
                                        return '0,00';
                                    })()}
                                </p>
                            )}

                            {/* Sélection de couleur */}
                            {colors.length > 1 && (
                                <div className="patricians-color-selection">
                                    <label className="patricians-label">COLOUR</label>
                                    <div className="patricians-color-options">
                                        {colors.map((color) => {
                                            const isSelected = selectedColor === color;
                                            return (
                                                <div
                                                    key={color}
                                                    className={`patricians-color-box ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        if (variantsByColor[color][0]) {
                                                            handleVariantChange(variantsByColor[color][0]);
                                                        }
                                                    }}
                                                >
                                                    <div 
                                                        className="patricians-color-preview"
                                                        style={{backgroundColor: convertColorToHex(color.toLowerCase())}}
                                                    ></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Sélection de taille */}
                            {sizesForColor.length > 0 && (
                                <div className="patricians-size-selection">
                                    <label className="patricians-label">SIZE</label>
                                    <div className="patricians-size-options">
                                        {sizesForColor.map((variant) => (
                                            <button
                                                key={variant.id}
                                                className={`patricians-size-box ${selectedVariant?.id === variant.id ? 'selected' : ''} ${variant.stock === 0 ? 'out-of-stock' : ''}`}
                                                onClick={() => variant.stock > 0 && handleVariantChange(variant)}
                                                disabled={variant.stock === 0}
                                            >
                                                {variant.size || 'ONE SIZE'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Bouton SELECT SIZE ou ADD TO CART */}
                            <button
                                className={`patricians-select-size-btn ${!isAvailable() ? 'disabled' : ''}`}
                                onClick={handleAddToCart}
                                disabled={!isAvailable()}
                            >
                                {(() => {
                                    if (!isAvailable()) {
                                        if (isProductInCommand()) {
                                            return "ALREADY IN CART";
                                        }
                                        return "OUT OF STOCK";
                                    }
                                    if (sizesForColor.length > 0 && !selectedVariant) {
                                        return "SELECT SIZE";
                                    }
                                    return "ADD TO CART";
                                })()}
                            </button>

                            {/* Caractéristiques */}
                            {product.description && typeof product.description === 'string' && (
                                <div className="patricians-product-features">
                                    {product.description.split('.').filter(f => f.trim()).map((feature, index) => (
                                        <p key={index} className="patricians-feature">
                                            {feature.trim().toUpperCase()}.
                                        </p>
                                    ))}
                                </div>
                            )}

                            {/* Liens */}
                            <div className="patricians-product-links">
                                <Link to="/shipping-policy" className="patricians-link">SHIPPING POLICY</Link>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const ProductPage = ({ currentCommand, setCurrentCommand }) => {
    return (
        <ProtectedPage key={`product-${currentCommand?.id}`} permissionsNeeded="client" needToBeAuthenticated={true} redirectPath="/client-secret">
            <ProductPageContent currentCommand={currentCommand} setCurrentCommand={setCurrentCommand} />
        </ProtectedPage>
    );
};

export default ProductPage;

